/**
 * ChatThread.tsx - Chat Thread Renderer
 *
 * Recursively renders chat conversation threads.
 * Handles nested responses and applies fade animations to newest messages.
 */

import { ChatBubble } from '@/ui/components/ChatBubble';
import { For } from "@chakra-ui/react";
import { extractResponseList } from '@/hooks/chat.utils';
import { useChatContext } from '@/contexts/ChatContext';
import { ReactFC } from '@/types/chat.types';
import React, { useMemo } from 'react';

interface FetchResponsesProps {
    author: string;
    item: any;
    isLast: boolean;
}

interface ChatThreadProps {
    responses: any[];
}

const FetchResponses: ReactFC<FetchResponsesProps> = React.memo(({ author, item, isLast }) => {
    const {
        postId,
        setLatestResponseId,
    } = useChatContext();

    const responseList = useMemo(() => extractResponseList(item, setLatestResponseId, postId), [item, setLatestResponseId, postId]);

    const isLastInChain = isLast && responseList.length === 0;
    const isAIGenerated = item.tags && item.tags.includes("ai generated");
    // Mark as new message if it's the last AI-generated message in the chain
    const isNewMessage = isAIGenerated && isLastInChain;

    return (
        <>
            {isAIGenerated ? (
                <ChatBubble
                    post={item}
                    boxOrientation="left"
                    shouldFade={isLastInChain}
                    isNewMessage={isNewMessage}
                />
            ) : (
                <ChatBubble
                    post={item}
                    boxOrientation="right"
                    shouldFade={isLastInChain}
                />
            )}

            {responseList.length > 0 && (
                <For each={responseList}>
                    {(itemInner, index) => (
                        <FetchResponses
                            author={author}
                            item={itemInner}
                            isLast={isLast && index === responseList.length - 1}
                            key={`${itemInner._id}-${index}`}
                        />
                    )}
                </For>
            )}
        </>
    );
});

export const ChatThread: ReactFC<ChatThreadProps> = React.memo(({ responses }) => {
    const {
        post,
        postId,
    } = useChatContext();

    const isInitialPostLast = responses.length === 0;

    return (
        <>
            <ChatBubble
                boxOrientation="right"
                post={post}
                shouldFade={isInitialPostLast}
            />

            {responses.length > 0 && (
                <For each={responses}>
                    {(item, index) => (
                        <FetchResponses
                            author={post?.author?.firstname || 'User'}
                            item={item}
                            isLast={index === responses.length - 1}
                            key={`${postId}-${index}`}
                        />
                    )}
                </For>
            )}
        </>
    );
});
